import { useCallback, useEffect, useMemo } from "react";
import { filter, map } from "lodash";
import {
  useCompleteOnboardingMutation,
  useCompleteOnboardingStepMutation,
} from "@/features/workspace/graphql/generated";
import { useViewer } from "@/features/workspace/hooks";
import { Viewer } from "@/features/auth/graphql/generated";
import {
  UserInvitePersonaType,
  UserPersonaType,
  UserSignupPersonaType,
} from "@/lib/onboarding/persona-types";
import { OnboardingTask, ONBOARDING_TASKS } from "@/lib/onboarding/tasks-map";

const getOnboardingPersona = (viewer: Viewer): UserPersonaType => {
  if (viewer.onboarding?.type) {
    // Validate the onboarding type is a valid onboarding persona
    if (
      Object.values(UserSignupPersonaType).includes(
        viewer.onboarding.type as UserSignupPersonaType
      )
    ) {
      return viewer.onboarding.type as UserSignupPersonaType;
    }

    if (
      Object.values(UserInvitePersonaType).includes(
        viewer.onboarding.type as UserInvitePersonaType
      )
    ) {
      return viewer.onboarding.type as UserInvitePersonaType;
    }
  }

  return UserInvitePersonaType.ORG_MEMBER;
};

const useOnboarding = () => {
  const { viewer } = useViewer();
  const isOnboardingCompleted =
    !viewer?.onboarding ||
    !!viewer?.onboarding?.completed_at ||
    !!viewer?.onboarding?.skipped_at;

  const onboardingPersona = getOnboardingPersona(viewer);

  const completedSteps = useMemo(
    () => viewer?.onboarding?.steps as string[],
    [viewer]
  );

  const surveyCompletion = viewer?.person?.completed_dim_types || [];

  const [completeOnboardingStep] = useCompleteOnboardingStepMutation({
    refetchQueries: ["Viewer"],
  });
  const [completeOnboarding] = useCompleteOnboardingMutation({
    refetchQueries: ["Viewer"],
  });

  const handleEmitOnboardingStep = useCallback(
    (step: OnboardingTask) => {
      if (completedSteps?.includes(step)) {
        return;
      }

      completeOnboardingStep({ variables: { step } });
    },
    [completedSteps, completeOnboardingStep]
  );

  const handleCompleteOnboarding = useCallback(() => {
    completeOnboarding();
  }, []);

  const onboardingTasks = ONBOARDING_TASKS.filter((task) =>
    task.personas?.includes(onboardingPersona as UserPersonaType)
  );

  const taskList = map(onboardingTasks, (task) => ({
    ...task,
    completed: completedSteps?.includes(task.id),
  }));

  useEffect(() => {
    if (
      !isOnboardingCompleted &&
      surveyCompletion?.length >= 3 // TODO - Should match the account, project or team surveys config
    ) {
      handleEmitOnboardingStep(OnboardingTask.COMPLETE_PROFILE);
    }
  }, [surveyCompletion, isOnboardingCompleted, handleEmitOnboardingStep]);

  useEffect(() => {
    if (
      !viewer.onboarding?.completed_at &&
      !viewer.onboarding?.skipped_at &&
      completedSteps?.length === taskList?.length
    ) {
      handleCompleteOnboarding();
    }
  }, [viewer?.onboarding?.completed_at, completedSteps?.length]);

  return {
    isOnboardingCompleted,
    onboardingPersona,
    completedSteps,
    taskList,
    handleEmitOnboardingStep,
    handleCompleteOnboarding,
  };
};

export default useOnboarding;
